import {
  useEffect,
  useRef,
  useState,
  useContext,
  useCallback,
  useMemo
} from 'react';
import { io } from 'socket.io-client';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { UserContext, SnackContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const HEARTBEAT_INTERVAL_MS = 30_000;

// options.autoAcquire (default true) — the classic single-pedigree behaviour:
//   the lock is taken as soon as the chart opens. The multi-pedigree-tabs
//   workspace passes false so opening is view-only (no lock); the lock is then
//   acquired lazily via the returned `acquire()` on the first edit of the active
//   tab, keeping "at most one edit lock in normal use" true across many open tabs.
//   With autoAcquire=false the hook still joins the socket room and reads the
//   current lock (GET) so the per-tab badge is correct.
// options.active (default true) — whether this is the visible/active tab. A
//   background VIEWER tab (not active, doesn't hold the lock) drops its socket to
//   avoid N idle connections + heartbeats; it reconnects and refreshes the badge
//   on activation. A background tab that HOLDS the lock keeps its socket (so the
//   lock stays alive) until the idle-release timer hands it back.
// TGCLOUD-1669 Phase 8 — one family lock, however many mounts hold it.
//
// THE BUG THIS EXISTS FOR. A lock's identity on the wire is (familyId, USER):
// the release route deletes by `{ familyId, lockedByUserId }` with no session
// term (backend/routes/pedigree-lock.js:97-100), and `isMine` below is computed
// from the row's user, so EVERY mount of mine on a family reports isMine true
// whether or not it acquired anything.
//
// That was harmless while a family could only be open once. Person tabs broke
// it: a person tab and its family's pedigree tab are two mounts on one
// familyId. So a BACKGROUND person tab would see isMine true (because my
// pedigree tab holds the lock), arm the 120s background idle release, and
// delete the lock out from under the tab I was actively editing in, which then
// told me another clinician had taken it.
//
// A sessionId term on the server cannot fix this: acquire overwrites sessionId
// on every re-acquire, so with two mounts only the last acquirer's id survives.
// The client has to know which MOUNT is holding, which is what this does.
//
// Module scope, not a ref: the two mounts are two component trees.
const familyLockHolders = new Map();

const addFamilyHolder = (familyId) => {
  const next = (familyLockHolders.get(familyId) || 0) + 1;
  familyLockHolders.set(familyId, next);
  return next === 1;
};

const dropFamilyHolder = (familyId) => {
  const next = (familyLockHolders.get(familyId) || 1) - 1;
  if (next <= 0) {
    familyLockHolders.delete(familyId);
    return true;
  }
  familyLockHolders.set(familyId, next);
  return false;
};

// Test seam. Module state leaks between tests without it.
export const __resetFamilyLockHolders = () => familyLockHolders.clear();

export function usePedigreeLock(familyId, options = {}) {
  const t = useAutoTranslation();

  const { autoAcquire = true, active = true } = options;
  const { user } = useContext(UserContext);
  const { setSnack } = useContext(SnackContext);
  const [lockState, setLockState] = useState(null);
  const autoAcquireRef = useRef(autoAcquire);
  autoAcquireRef.current = autoAcquire;
  // Has the lock for THIS familyId been read yet? lockState starts null and is
  // filled only after the socket connects and the first acquire (classic) or
  // GET (workspace) answers, so until then `isLocked` downstream reads false
  // whoever holds the family. Anything that must not act on an unknown lock —
  // the chart's automatic placeholder save (TGCLOUD-1813) — waits on this.
  // True only on a SUCCESSFUL read. A failed one leaves lockState null, and
  // downstream null-because-unknown is indistinguishable from
  // null-because-free: resolving on it would let that save write into a
  // family whose holder was never learned (the config POST has no server-side
  // lock check). Left false, the save stays deferred — the user's first edit
  // saves as before — and a later successful read (a socket reconnect re-runs
  // the connect handler) still resolves it. False again the moment familyId
  // changes; a late answer for the PREVIOUS family must not mark the new one
  // resolved, hence the ref check.
  const [lockResolved, setLockResolved] = useState(false);
  const familyIdRef = useRef(familyId);
  familyIdRef.current = familyId;
  useEffect(() => {
    setLockResolved(false);
  }, [familyId]);

  // Pending access-request state: null | 'pending' | 'granted' | 'denied'
  const [requestStatus, setRequestStatus] = useState(null);

  // Incoming request from another user (only set on the lock holder's side)
  // null | { requesterName: string, requesterId: string }
  const [pendingRequest, setPendingRequest] = useState(null);

  const socketRef = useRef(null);
  const heartbeatRef = useRef(null);
  const sessionId = useRef(String(Date.now() + Math.random()));

  // Does THIS mount hold the lock? A ref for the synchronous release paths
  // (cleanup, beacon) and state for the effects that must re-run when it
  // changes. `isMine` is not a substitute: it says the lock is MINE, not that
  // this mount is the one holding it.
  const holdsRef = useRef(false);
  const [holdsLock, setHoldsLock] = useState(false);
  const markHolding = useCallback((familyKey) => {
    if (holdsRef.current) return;
    holdsRef.current = true;
    addFamilyHolder(familyKey);
    setHoldsLock(true);
  }, []);
  // Returns true when this was the LAST holder and the server should be told.
  const dropHolding = useCallback((familyKey) => {
    if (!holdsRef.current) return false;
    holdsRef.current = false;
    setHoldsLock(false);
    return dropFamilyHolder(familyKey);
  }, []);

  const userId = user?._id ? String(user._id) : null;
  const isMine = lockState
    ? String(lockState.lockedByUserId) === userId
    : false;
  // The socket handlers below are installed ONCE per (familyId, wantsSocket)
  // and close over whatever `userId` was at that moment, which on a cold load
  // is often null (UserContext fills in after the first render). A handler that
  // has to decide "is this lock now MINE?" therefore reads the ref, not the
  // captured value.
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  // Keep a live socket when: the classic single-chart flow (autoAcquire), this is
  // the active tab, or this backgrounded tab still holds the lock. `active||isMine`
  // stays true across the isMine flip on acquire (active is true then), so
  // acquiring never tears down/recreates the socket.
  const wantsSocket = autoAcquire || active || isMine;

  useEffect(() => {
    if (!familyId || !wantsSocket) return;

    const socket = io('', { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      // Join pedigree room after socket connects
      socket.emit('pedigree:join', { familyId });

      // The first read of this family's lock has ANSWERED — called from the
      // success branches only, never from a catch (see lockResolved above).
      // Guarded on the family still being the one asked about.
      const markResolved = () => {
        if (familyIdRef.current === familyId) setLockResolved(true);
      };

      if (autoAcquireRef.current) {
        // Classic behaviour: acquire the lock on open.
        axios
          .post(`${APICONSTANTS.pedigreeLock}/${familyId}/acquire`, {
            sessionId: socket.id || sessionId.current
          })
          .then((res) => {
            setLockState(res.data.lock);
            // REGISTER AS THE HOLDER. Without this the refcount stays at zero
            // for the classic single-chart path, which is the ONLY acquire a
            // user outside the workspace ever makes, and every release is then
            // suppressed: the socket teardown, the beforeunload beacon and
            // releaseLock alike. The lock survived on the 90s TTL and no
            // pedigree:lock-released was ever emitted, so colleagues stayed
            // locked out of a pedigree nobody had open.
            markHolding(familyId);
            markResolved();
          })
          .catch((err) =>
            console.error('Failed to acquire pedigree lock', err)
          );
      } else {
        // Workspace view-only open: just read the current lock so the badge is
        // right; the lock is acquired lazily on the first edit (see `acquire`).
        axios
          .get(`${APICONSTANTS.pedigreeLock}/${familyId}`)
          .then((res) => {
            setLockState(res.data || null);
            markResolved();
          })
          .catch(() => {});
      }
    });

    // Lock events from other users
    socket.on('pedigree:lock-acquired', ({ lock }) => {
      setLockState(lock);
    });

    socket.on('pedigree:lock-released', () => {
      setLockState(null);
    });

    socket.on('pedigree:lock-force-acquired', ({ lock }) => {
      setLockState(lock);
      // A force-acquire is broadcast to the whole room, INCLUDING the mount
      // that asked for it — that is how the requester's own client learns the
      // row is now theirs when the force was triggered from another of their
      // mounts. Registering the holder here as well as in forceAcquireLock is
      // what keeps the refcount right for that second mount; markHolding is
      // idempotent per mount, so the mount that already registered in the HTTP
      // response is unaffected. Without it the same defect as below applies:
      // a lock this user holds that no release path will ever hand back.
      if (lock && String(lock.lockedByUserId) === userIdRef.current) {
        markHolding(familyId);
      }
    });

    // Someone is requesting edit access from us — show persistent banner
    socket.on(
      'pedigree:lock-request-received',
      ({ requesterName, requesterId, targetUserId }) => {
        if (targetUserId && userId && String(targetUserId) !== userId) return;
        setPendingRequest({ requesterName, requesterId });
      }
    );

    // Requester cancelled their access request — clear our banner
    socket.on(
      'pedigree:lock-request-cancelled',
      ({ requesterId, targetUserId }) => {
        if (targetUserId && userId && String(targetUserId) !== userId) return;
        setPendingRequest((prev) =>
          prev && String(prev.requesterId) === String(requesterId) ? null : prev
        );
      }
    );

    // Response from lock holder to our request
    socket.on(
      'pedigree:lock-request-result',
      ({ granted, targetUserId, declineMessage }) => {
        if (targetUserId && userId && String(targetUserId) !== userId) return;
        setRequestStatus(granted ? 'granted' : 'denied');
        if (granted) {
          // Re-acquire lock now that holder released it
          axios
            .post(`${APICONSTANTS.pedigreeLock}/${familyId}/acquire`, {
              sessionId: socket.id || sessionId.current
            })
            .then((res) => {
              setLockState(res.data.lock);
              markHolding(familyId);
            })
            .catch(console.error);
        } else {
          const msg = declineMessage
            ? t('Your request was declined: "{{declineMessage}}"', {
                declineMessage
              })
            : t('Your request for edit access was declined.');
          setSnack({ status: true, severity: 'warning', msg });
        }
      }
    );

    // Start heartbeat
    heartbeatRef.current = setInterval(() => {
      axios
        .post(`${APICONSTANTS.pedigreeLock}/${familyId}/heartbeat`)
        .catch(console.error);
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(heartbeatRef.current);
      socket.emit('pedigree:leave', { familyId });
      // Was unconditional, which meant a tab that never acquired anything
      // still deleted the row on teardown.
      if (dropHolding(familyId)) {
        axios
          .post(`${APICONSTANTS.pedigreeLock}/${familyId}/release`)
          .catch(console.error);
      }
      socket.disconnect();
      socketRef.current = null;
    };
  }, [familyId, wantsSocket]); // eslint-disable-line react-hooks/exhaustive-deps

  // Release lock on tab close via sendBeacon
  useEffect(() => {
    if (!familyId) return;
    const handleUnload = () => {
      // Only from a mount that actually holds it. On a real unload every mount
      // is going away, so the last holder's beacon is the one that matters.
      if (!holdsRef.current) return;
      navigator.sendBeacon(`/api/pedigree-lock/${familyId}/release`);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [familyId]);

  // Lazily acquire the edit lock (workspace mode: called on the first edit of the
  // active tab). Idempotent — the backend upserts a same-user re-acquire, so
  // calling it when we already hold the lock is a no-op. Returns whether we now
  // hold it (false if another active user holds it).
  const acquire = useCallback(async () => {
    try {
      const res = await axios.post(
        `${APICONSTANTS.pedigreeLock}/${familyId}/acquire`,
        { sessionId: socketRef.current?.id || sessionId.current }
      );
      if (res.data?.lock) setLockState(res.data.lock);
      const acquired = res.data?.acquired !== false;
      if (acquired) markHolding(familyId);
      return acquired;
    } catch (err) {
      console.error('Failed to acquire pedigree lock', err);
      return false;
    }
  }, [familyId, markHolding]);

  const releaseLock = useCallback(() => {
    // Only the mount that took it, and only when no other mount still needs
    // it. Releasing on `isMine` alone is what let one tab delete another's.
    if (!dropHolding(familyId)) return;
    axios
      .post(`${APICONSTANTS.pedigreeLock}/${familyId}/release`)
      .then(() => setLockState((prev) => (isMine ? null : prev)))
      .catch(console.error);
  }, [familyId, isMine, dropHolding]);

  // Lock holder responds to an incoming request (grant or decline with message)
  const respondToRequest = useCallback(
    (granted, declineMessage) => {
      const req = pendingRequest;
      if (!req) return;
      setPendingRequest(null);
      const socket = socketRef.current;
      if (socket) {
        socket.emit('pedigree:lock-release-response', {
          familyId,
          granted,
          requesterId: req.requesterId,
          declineMessage: declineMessage || null
        });
      }
      if (granted) {
        // Release our lock so the requester can acquire it
        axios
          .post(`${APICONSTANTS.pedigreeLock}/${familyId}/release`)
          .catch(console.error);
      }
    },
    [familyId, pendingRequest]
  );

  const requestLock = useCallback(() => {
    if (!lockState) return;
    setRequestStatus('pending');
    // Use the HTTP route so the backend resolves the name from the session
    axios
      .post(`${APICONSTANTS.pedigreeLock}/${familyId}/request`, {
        lockHolderUserId: lockState.lockedByUserId
      })
      .catch(() => setRequestStatus(null));
  }, [familyId, lockState]);

  const cancelRequestLock = useCallback(() => {
    if (!lockState) return;
    setRequestStatus(null);
    const socket = socketRef.current;
    if (socket) {
      socket.emit('pedigree:lock-request-cancel', {
        familyId,
        lockHolderUserId: lockState.lockedByUserId
      });
    }
  }, [familyId, lockState]);

  const forceAcquireLock = useCallback(() => {
    axios
      .post(`${APICONSTANTS.pedigreeLock}/${familyId}/force-acquire`, {
        sessionId: socketRef.current?.id || sessionId.current
      })
      .then((res) => {
        setLockState(res.data.lock);
        // REGISTER AS THE HOLDER, exactly as the two acquire paths above do.
        //
        // THE DEFECT THIS FIXES. Every release path is gated on `holdsRef` —
        // the socket teardown, the beforeunload beacon, releaseLock (the tab
        // menu's "Release lock") and the background idle release, which reads
        // the `holdsLock` state this sets. forceAcquireLock set the lock state
        // and nothing else, so after a force-take this mount held a real server
        // row while believing it held nothing, and NONE of those paths fired.
        // The colleague whose lock had just been taken then waited out the full
        // 90s TTL with no pedigree:lock-released ever emitted, and closing the
        // tab did not give it back either. 7.1.0 released unconditionally on
        // teardown, so this is a regression introduced with the refcount.
        markHolding(familyId);
      })
      .catch((err) =>
        console.error('Failed to force-acquire pedigree lock', err)
      );
  }, [familyId, markHolding]);

  // MEMOISED, and it is not cosmetic (TGCLOUD-1775).
  //
  // A bare object literal here gets a new identity on every render, and this
  // object is threaded down PedigreeChartView -> PedigreeBox -> PedigreeChart
  // -> ChartBox, where several memos depend on it. So it invalidated all of
  // them on every render, which is why a measured tab switch committed EVERY
  // open tab rather than the two whose active flag moved. Every member below is
  // already a useState value or a useCallback, so this only stops the wrapper
  // churning.
  return useMemo(
    () => ({
      lockState,
      isMine,
      // Whether THIS mount is the one holding the lock, as opposed to isMine,
      // which only says the lock belongs to this user. The background idle
      // release has to gate on this one.
      holdsLock,
      // Whether the lock has been READ at all for this family (see above).
      lockResolved,
      requestStatus,
      requestLock,
      cancelRequestLock,
      forceAcquireLock,
      pendingRequest,
      respondToRequest,
      acquire,
      releaseLock
    }),
    [
      lockState,
      isMine,
      holdsLock,
      lockResolved,
      requestStatus,
      requestLock,
      cancelRequestLock,
      forceAcquireLock,
      pendingRequest,
      respondToRequest,
      acquire,
      releaseLock
    ]
  );
}
